import { useMemo, useRef, useState } from 'react';
import { Empty, ErrorBanner, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { formatDate, toLocalDateString, todayLocal } from '../lib/format';
import { useResource } from '../lib/hooks';
import { readSpreadsheet, type Sheet } from '../lib/spreadsheet';
import {
  audit,
  guessColumns,
  type AuditResult,
  type AuditRow,
  type ColumnMap,
  type Finding,
  type Taxonomy,
} from './tickets/audit';
import { RulesEditor } from './tickets/RulesEditor';
import type { Me } from '../lib/types';

/**
 * Ticket Audit — check a ticket export against the rules.
 *
 * THE FILE NEVER LEAVES THE BROWSER. A ticket export carries client names and
 * subject lines; the audit is arithmetic over five columns and needs no
 * server, so there is no upload endpoint, nothing written to the database and
 * nothing to retain. Only the RULES are stored, and they came from a
 * spreadsheet rather than from anybody's tickets.
 *
 * The four rules, anchored on ticket category, live in `tickets/audit.ts`.
 */

const FIELD_LABEL: Record<keyof ColumnMap, string> = {
  category: 'Ticket category',
  status: 'Status',
  workType: 'Work type',
  issueType: 'Issue type',
  subIssue: 'Sub-issue',
  reference: 'Ticket number',
};

/* The anchor and the four things checked against it. `reference` is not a rule
   — it is only so a finding can name the ticket it is about. */
const MAPPABLE: (keyof ColumnMap)[] = [
  'reference',
  'category',
  'status',
  'workType',
  'issueType',
  'subIssue',
];

/**
 * What the results table is currently showing.
 *
 * One value rather than a pile of booleans: the summary numbers and the table
 * are the same question asked twice, and two flags that can both be on would
 * let them disagree about which rows are on screen.
 */
type Filter =
  | { kind: 'all' }
  | { kind: 'problems' }
  | { kind: 'violations' }
  | { kind: 'unknowns' }
  | { kind: 'clean' }
  | { kind: 'field'; field: Finding['field'] };

const sameFilter = (a: Filter, b: Filter): boolean =>
  a.kind === b.kind && (a.kind !== 'field' || b.kind !== 'field' || a.field === b.field);

/** The heading over the table, so it always says what is being looked at. */
function filterTitle(f: Filter): string {
  switch (f.kind) {
    case 'all':
      return 'Every row';
    case 'problems':
      return 'Rows with something to fix';
    case 'violations':
      return 'Rows that break a rule';
    case 'unknowns':
      return 'Rows that cannot be checked';
    case 'clean':
      return 'Rows that follow every rule';
    case 'field': {
      const what = FIELD_LABEL[f.field].toLowerCase();
      return `Rows with ${/^[aeiou]/.test(what) ? 'an' : 'a'} ${what} problem`;
    }
  }
}

const hasViolation = (r: AuditRow) => r.findings.some((x) => x.severity === 'violation');
const hasUnknown = (r: AuditRow) => r.findings.some((x) => x.severity === 'unknown');

function matches(r: AuditRow, f: Filter): boolean {
  switch (f.kind) {
    case 'all':
      return true;
    case 'problems':
      return r.findings.length > 0;
    case 'violations':
      return hasViolation(r);
    case 'unknowns':
      /* The card counts rows that are ONLY unverifiable — a row that also
         breaks a rule is counted under the rule it breaks, or the two cards
         would add up to more than the file. The filter has to agree. */
      return !hasViolation(r) && hasUnknown(r);
    case 'clean':
      return r.findings.length === 0;
    case 'field':
      return r.findings.some((x) => x.field === f.field && x.severity === 'violation');
  }
}

interface TaxonomyPayload {
  doc: Taxonomy | null;
  sourceName: string | null;
  updatedUtc: string | null;
  updatedBy: string | null;
}

export function TicketAuditPage() {
  const taxonomy = useResource<TaxonomyPayload>('/tickets/taxonomy');
  const fileInput = useRef<HTMLInputElement>(null);

  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [map, setMap] = useState<ColumnMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [editRules, setEditRules] = useState(false);
  const [filter, setFilter] = useState<Filter>({ kind: 'problems' });

  /* Pulling straight from Autotask instead of an export. Defaults to the last
     seven days, the usual "what did the desk file this week" question. */
  const [atFrom, setAtFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return toLocalDateString(d);
  });
  const [atTo, setAtTo] = useState(todayLocal());
  const [atBasis, setAtBasis] = useState<'created' | 'completed'>('created');

  /* Editing changes what every audit reports for everybody, so the button is
     admin-only — and so is the endpoint behind it, which is what actually
     enforces it. */
  const me = useResource<Me>('/me');
  /* Autotask's web address, so each ticket number opens the ticket there. */
  const atWeb = useResource<{ webUrl: string | null }>('/tickets/autotask/web-url');
  const ticketHref = (ref: string) =>
    atWeb.data?.webUrl && ref
      ? `${atWeb.data.webUrl}Autotask/AutotaskExtend/ExecuteCommand.aspx?${new URLSearchParams({ Code: 'OpenTicketDetail', TicketNumber: ref })}`
      : null;
  const isAdmin = me.data?.appRole === 'admin';

  const tax = taxonomy.data?.doc ?? null;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const s = await readSpreadsheet(file);
      if (s.headers.length === 0) throw new Error('That file has no header row.');
      setSheet(s);
      setFileName(file.name);
      setMap(guessColumns(s.headers));
      /* A filter from the last file would silently hide rows of this one. */
      setFilter({ kind: 'problems' });
    } catch (e) {
      setSheet(null);
      setMap(null);
      setError(e instanceof Error ? e.message : 'Could not read that file');
    } finally {
      setBusy(false);
    }
  };

  /** The chosen days, midnight to midnight in this browser, as UTC instants. */
  const pull = async () => {
    setError(null);
    setBusy(true);
    try {
      const from = new Date(`${atFrom}T00:00:00`);
      const to = new Date(`${atTo}T00:00:00`);
      to.setDate(to.getDate() + 1);
      const s = await api.get<{ headers: string[]; rows: string[][] }>(
        `/tickets/autotask?${new URLSearchParams({ from: from.toISOString(), to: to.toISOString(), basis: atBasis })}`,
      );
      setSheet({ headers: s.headers, rows: s.rows, sheetName: null });
      setFileName(
        `Autotask · ${atBasis === 'completed' ? 'completed' : 'created'} ${formatDate(atFrom)}${atFrom === atTo ? '' : ` to ${formatDate(atTo)}`}`,
      );
      setMap(guessColumns(s.headers));
      setFilter({ kind: 'problems' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read tickets from Autotask');
    } finally {
      setBusy(false);
    }
  };

  const result: AuditResult | null = useMemo(() => {
    if (!sheet || !map || !tax) return null;
    if (map.category === null) return null;
    return audit(sheet.rows, map, tax);
  }, [sheet, map, tax]);

  const shown = useMemo(
    () => (result ? result.rows.filter((r) => matches(r, filter)) : []),
    [result, filter],
  );

  if (taxonomy.loading) return <Loading label="Loading the rules" />;

  return (
    <>
      <PageHead
        title="Ticket Audit"
        lede="Upload a ticket export, or pull tickets straight from Autotask, and see which rows break the rules. Ticket category is the anchor: it decides the statuses, the work types and the issue types allowed, and the sub-issue has to belong to the issue type it is filed under."
        actions={
          tax ? (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setShowRules(true)}>
                Rules
              </button>
              {/* Not admin-gated: anyone who can read the rules can take a
                  copy of them. Reviewing them elsewhere is not editing them. */}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => downloadRules(tax, taxonomy.data?.sourceName ?? null)}
              >
                Export rules
              </button>
              {isAdmin && (
                <button type="button" className="btn btn-ghost" onClick={() => setEditRules(true)}>
                  Edit rules
                </button>
              )}
            </>
          ) : undefined
        }
      />

      {error && <ErrorBanner message={error} />}
      {taxonomy.error && <ErrorBanner message={taxonomy.error} />}

      {!tax && !taxonomy.error && (
        <Empty title="No rules loaded">
          <p className="muted">
            The taxonomy has not been imported for this organisation yet, so there is nothing to
            check a file against.
          </p>
        </Empty>
      )}

      {tax && (
        <>
          <section className="card mb-4">
            <div className="card-body">
              <div className="row-between wrap">
                <div className="grow">
                  <p className="small">
                    Pick a <strong>.xlsx</strong> or <strong>.csv</strong> of tickets. It is read in
                    this browser and never uploaded — nothing is stored.
                  </p>
                  <p className="xs muted mt-2">
                    Checking against <strong>{taxonomy.data?.sourceName ?? 'the loaded rules'}</strong>
                    {taxonomy.data?.updatedUtc
                      ? ` · loaded ${formatDate(taxonomy.data.updatedUtc)}`
                      : ''}
                    {' · '}
                    {tax.categories.length} categories, {tax.statuses.length} statuses,{' '}
                    {tax.workTypes.length} work types, {tax.issueTypes.length} issue types
                  </p>
                </div>
                <div className="row wrap">
                  <input
                    ref={fileInput}
                    type="file"
                    accept=".xlsx,.csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      void pick(e.target.files?.[0]);
                      /* Cleared so picking the same file twice re-runs it —
                         the obvious thing to do after fixing the tickets. */
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy}
                    onClick={() => fileInput.current?.click()}
                  >
                    {busy ? 'Reading…' : sheet ? 'Choose another file' : 'Choose a file'}
                  </button>
                  {result && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => downloadFindings(result, fileName ?? 'tickets')}
                    >
                      Export findings
                    </button>
                  )}
                </div>
              </div>

              <div className="ta-autotask mt-4">
                <p className="small">
                  <strong>Or pull tickets from Autotask</strong> for a date range — up to six months.
                  Read live and checked here; nothing is stored.
                </p>
                <div className="row wrap mt-2" style={{ alignItems: 'flex-end' }}>
                  <Field label="Tickets">
                    <select
                      className="select"
                      value={atBasis}
                      onChange={(e) => setAtBasis(e.target.value as 'created' | 'completed')}
                    >
                      <option value="created">Created</option>
                      <option value="completed">Completed</option>
                    </select>
                  </Field>
                  <Field label="From">
                    <input
                      className="input"
                      type="date"
                      value={atFrom}
                      max={atTo}
                      onChange={(e) => setAtFrom(e.target.value)}
                    />
                  </Field>
                  <Field label="To">
                    <input
                      className="input"
                      type="date"
                      value={atTo}
                      min={atFrom}
                      max={todayLocal()}
                      onChange={(e) => setAtTo(e.target.value)}
                    />
                  </Field>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy || !atFrom || !atTo || atFrom > atTo}
                    onClick={() => void pull()}
                  >
                    {busy ? 'Reading…' : 'Pull from Autotask'}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {sheet && map && (
            <section className="card mb-4">
              <div className="card-head">
                <div className="grow">
                  <span className="eyebrow">Columns</span>
                  <h3>{fileName}</h3>
                  <p className="sub">
                    {sheet.rows.length} rows
                    {sheet.sheetName ? ` from "${sheet.sheetName}"` : ''} · matched by header name,
                    correct anything it guessed wrong.
                  </p>
                </div>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  {MAPPABLE.map((f) => (
                    <Field key={f} label={FIELD_LABEL[f]}>
                      <select
                        className="select"
                        value={map[f] ?? ''}
                        onChange={(e) =>
                          setMap({ ...map, [f]: e.target.value === '' ? null : Number(e.target.value) })
                        }
                      >
                        <option value="">— not in this file —</option>
                        {sheet.headers.map((h, i) => (
                          <option key={i} value={i}>
                            {h || `(column ${i + 1})`}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>
                {map.category === null && (
                  <p className="risk-note xs mt-4">
                    <strong>Ticket category is required. </strong>
                    It is the anchor for every other rule — without it there is nothing to check a
                    status, work type or issue type against.
                  </p>
                )}
              </div>
            </section>
          )}

          {result && <Summary result={result} filter={filter} onFilter={setFilter} />}

          {result && (
            <section className="card">
              <div className="card-head">
                <div className="grow">
                  <h3>{filterTitle(filter)}</h3>
                  <div className="sub">
                    Showing {shown.length} of {result.checked}.
                    {filter.kind !== 'all' && ' Pick a figure above to look at a different set.'}
                  </div>
                </div>
                {filter.kind !== 'all' && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setFilter({ kind: 'all' })}
                  >
                    Show every row
                  </button>
                )}
              </div>

              {shown.length === 0 ? (
                <div className="card-body">
                  {/* "Nothing to fix" is only true for the filters that mean
                      it. Saying it under a field filter would claim the file
                      is clean when the reader has just narrowed to one column. */}
                  <p className="muted small">
                    {filter.kind === 'problems' || filter.kind === 'violations'
                      ? 'Nothing to fix — every row follows the rules.'
                      : 'No rows match that.'}
                  </p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="table ta-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Row</th>
                        <th className="ta-ref">Ticket</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Work type</th>
                        <th>Issue type</th>
                        <th>Sub-issue</th>
                        <th className="ta-why">What is wrong</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((r) => {
                        const bad = (f: string) =>
                          r.findings.some((x) => x.field === f && x.severity === 'violation');
                        const iffy = (f: string) =>
                          r.findings.some((x) => x.field === f && x.severity === 'unknown');
                        const cls = (f: string) =>
                          bad(f) ? 'ta-bad' : iffy(f) ? 'ta-unknown' : undefined;
                        return (
                          <tr key={r.line}>
                            <td className="mono xs subtle">{r.line}</td>
                            <td className="mono xs ta-ref">
                              {ticketHref(r.reference) ? (
                                <a href={ticketHref(r.reference)!} target="_blank" rel="noreferrer" title="Open this ticket in Autotask">
                                  {r.reference}
                                </a>
                              ) : (
                                r.reference || '—'
                              )}
                            </td>
                            <td className={cls('category')}>{r.category || <span className="subtle">—</span>}</td>
                            <td className={cls('status')}>{r.status || <span className="subtle">—</span>}</td>
                            <td className={cls('workType')}>{r.workType || <span className="subtle">—</span>}</td>
                            <td className={cls('issueType')}>{r.issueType || <span className="subtle">—</span>}</td>
                            <td className={cls('subIssue')}>{r.subIssue || <span className="subtle">—</span>}</td>
                            <td className="ta-why">
                              <ul className="ta-findings xs">
                                {r.findings.map((f, i) => (
                                  <li key={i} className={f.severity === 'violation' ? 'flagged' : 'muted'}>
                                    {f.message}
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {showRules && tax && <RulesDialog tax={tax} onClose={() => setShowRules(false)} />}

      {editRules && tax && (
        <RulesEditor
          taxonomy={tax}
          sourceName={taxonomy.data?.sourceName ?? null}
          onClose={() => setEditRules(false)}
          onSaved={() => taxonomy.reload()}
        />
      )}
    </>
  );
}

/* =========================================================================
   Summary
   ========================================================================= */

/** Counts first, because the question is always "how bad is it" before "which
 *  rows". Violations and unknowns are kept apart: a gap in the rules is not a
 *  mistake somebody made. */
/** One summary figure, and the set of rows behind it. Defined out here rather
 *  than inside Summary: a component declared during render is a new type each
 *  time, so React would unmount and remount all four on every click. */
function StatPick({
  label,
  value,
  foot,
  accent,
  to,
  filter,
  onPick,
}: {
  label: string;
  value: number;
  foot: string;
  accent?: boolean;
  to: Filter;
  filter: Filter;
  onPick: (f: Filter) => void;
}) {
  const on = sameFilter(filter, to);
  return (
    <button
      type="button"
      className={`stat stat-pick${on ? ' is-active' : ''}`}
      onClick={() => onPick(to)}
      aria-pressed={on}
    >
      <div className="label">{label}</div>
      <div className={`figure${accent ? ' accent' : ''}`}>{value}</div>
      <div className="foot">{foot}</div>
    </button>
  );
}

function Summary({
  result,
  filter,
  onFilter,
}: {
  result: AuditResult;
  filter: Filter;
  onFilter: (f: Filter) => void;
}) {
  const f = result.byField;

  /* Every figure narrows the table to the rows behind it — the number and the
     list are the same answer, so reading one should get you the other.
     Clicking the active one clears it, because the way back is where you
     came from. */
  const pick = (next: Filter) => onFilter(sameFilter(filter, next) ? { kind: 'all' } : next);

  return (
    <>
      <div className="grid grid-4 mb-4">
        <StatPick
          label="Rows checked"
          value={result.checked}
          foot="in the file"
          to={{ kind: 'all' }}
          filter={filter}
          onPick={pick}
        />
        <StatPick
          label="Break a rule"
          value={result.withViolations}
          foot="need fixing"
          accent={result.withViolations > 0}
          to={{ kind: 'violations' }}
          filter={filter}
          onPick={pick}
        />
        <StatPick
          label="Cannot be checked"
          value={result.withUnknowns}
          foot="the rules do not say"
          to={{ kind: 'unknowns' }}
          filter={filter}
          onPick={pick}
        />
        <StatPick
          label="Clean"
          value={result.clean}
          foot="follow every rule"
          to={{ kind: 'clean' }}
          filter={filter}
          onPick={pick}
        />
      </div>

      {result.withViolations > 0 && (
        <div className="card mb-4">
          <div className="card-body tight">
            <span className="eyebrow">Violations by field</span>
            <div className="row wrap mt-2">
              {(Object.keys(f) as (keyof typeof f)[])
                .filter((k) => f[k] > 0)
                .map((k) => {
                  const to: Filter = { kind: 'field', field: k };
                  const on = sameFilter(filter, to);
                  return (
                    <button
                      key={k}
                      type="button"
                      className={`badge badge-critical badge-pick${on ? ' is-active' : ''}`}
                      onClick={() => pick(to)}
                      aria-pressed={on}
                    >
                      {FIELD_LABEL[k]}: {f[k]}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* =========================================================================
   The rules themselves
   ========================================================================= */

/** What is allowed, per category. Read before disputing a finding, which is
 *  why it names the counts rather than only listing them. */
function RulesDialog({ tax, onClose }: { tax: Taxonomy; onClose: () => void }) {
  const [cat, setCat] = useState(tax.categories[0] ?? '');

  const statuses = tax.statuses.filter((s) => s.categories.includes(cat));
  const workTypes = tax.workTypes.filter((w) => w.categories.includes(cat));
  const issueTypes = tax.issueTypes.filter((i) => i.categories.includes(cat));

  return (
    <Modal title="What is allowed" onClose={onClose}>
      <Field label="Ticket category">
        <select className="select" value={cat} onChange={(e) => setCat(e.target.value)}>
          {tax.categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      <div className="stack mt-4" style={{ gap: 'var(--space-4)' }}>
        <RuleList
          title={`Statuses (${statuses.length})`}
          names={statuses.map((s) => s.name)}
          empty="No statuses listed for this category, so a status cannot be checked against it."
        />
        <RuleList
          title={`Work types (${workTypes.length})`}
          names={workTypes.map((w) => w.name)}
          empty="No work types listed for this category, so a work type cannot be checked against it."
        />
        <div>
          <span className="eyebrow">Issue types ({issueTypes.length})</span>
          {issueTypes.length === 0 ? (
            <p className="xs muted">No issue types listed for this category.</p>
          ) : (
            <div className="stack mt-2" style={{ gap: 'var(--space-2)' }}>
              {issueTypes.map((i) => (
                <div key={i.name} className="mini-row rec-q">
                  <span className="strong small">{i.name}</span>
                  {i.subIssues.length === 0 ? (
                    <span className="xs muted">
                      No sub-issues listed — a sub-issue on this cannot be checked.
                    </span>
                  ) : (
                    <span className="xs muted">{i.subIssues.join(' · ')}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function RuleList({ title, names, empty }: { title: string; names: string[]; empty: string }) {
  return (
    <div>
      <span className="eyebrow">{title}</span>
      {names.length === 0 ? (
        <p className="xs muted">{empty}</p>
      ) : (
        <div className="row wrap mt-2">
          {names.map((n) => (
            <span key={n} className="badge badge-neutral">
              {n}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Export
   ========================================================================= */

const esc = (s: string) => `"${String(s ?? '').replace(/"/g, '""')}"`;

/** Hand a generated CSV to the browser. Same shape as every other export on
 *  this page: made here, never round-tripped through the server. The BOM is
 *  what makes Excel open a UTF-8 CSV without mangling the em dashes. */
function downloadCsv(lines: string[], name: string) {
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * The rules as CSV — one row per rule, not one per list.
 *
 * "Category X permits status Y" is the thing being stated, so that is the row.
 * Flattened this way the whole taxonomy sorts, filters and pivots in Excel,
 * which is what somebody reviewing it actually does. Four separate blocks
 * would just be the source spreadsheet again, and that already exists.
 *
 * SUB-ISSUES CARRY NO CATEGORY, because they do not have one: a sub-issue
 * belongs to its issue type and reaches a category only through it. Filling in
 * a category for them would state a rule that does not exist.
 *
 * ITEMS WITH NO CATEGORY ARE INCLUDED, marked rather than dropped. They are
 * the gaps in the rules, and an export that quietly omitted them would read as
 * a complete rulebook.
 */
function downloadRules(tax: Taxonomy, sourceName: string | null) {
  const lines = [
    ['Rule', 'Ticket category', 'Item', 'Belongs to issue type', 'SLA event', 'Active']
      .map(esc)
      .join(','),
  ];

  const NONE = '(none — cannot be checked)';
  const push = (rule: string, cat: string, item: string, issue = '', sla = '', active = '') =>
    lines.push([rule, cat, item, issue, sla, active].map(esc).join(','));

  for (const st of tax.statuses as (typeof tax.statuses[number] & {
    slaEvent?: string | null;
    active?: boolean;
  })[]) {
    const sla = st.slaEvent ?? '';
    const active = st.active === undefined ? '' : String(st.active);
    if (st.categories.length === 0) push('Status', NONE, st.name, '', sla, active);
    for (const c of st.categories) push('Status', c, st.name, '', sla, active);
  }

  for (const wt of tax.workTypes) {
    if (wt.categories.length === 0) push('Work type', NONE, wt.name);
    for (const c of wt.categories) push('Work type', c, wt.name);
  }

  for (const it of tax.issueTypes as (typeof tax.issueTypes[number] & { active?: boolean })[]) {
    const active = it.active === undefined ? '' : String(it.active);
    if (it.categories.length === 0) push('Issue type', NONE, it.name, '', '', active);
    for (const c of it.categories) push('Issue type', c, it.name, '', '', active);
    for (const sub of it.subIssues) push('Sub-issue', '', sub, it.name);
  }

  const base = (sourceName ?? 'Ticket rules').replace(/\.[^.]+$/, '');
  downloadCsv(lines, `${base} — rules.csv`);
}

/** The findings as CSV — one row per problem, not per ticket, so it can be
 *  sorted and assigned. Generated in the browser like everything else here. */
function downloadFindings(result: AuditResult, fileName: string) {
  const lines = [
    ['Row', 'Ticket', 'Field', 'Severity', 'Value', 'Problem', 'Category', 'Status', 'Work type', 'Issue type', 'Sub-issue']
      .map(esc)
      .join(','),
  ];
  for (const r of result.rows) {
    for (const f of r.findings) {
      lines.push(
        [
          String(r.line),
          r.reference,
          FIELD_LABEL[f.field],
          f.severity === 'violation' ? 'Breaks a rule' : 'Cannot be checked',
          f.value,
          f.message,
          r.category,
          r.status,
          r.workType,
          r.issueType,
          r.subIssue,
        ]
          .map(esc)
          .join(','),
      );
    }
  }

  downloadCsv(lines, `${fileName.replace(/\.[^.]+$/, '')} — audit findings.csv`);
}
